import React, { useState, useEffect, useCallback } from 'react';
import { Award, Plus, Edit, Trash2, RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react';
import axios from 'axios';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const IS_PROD = !window.location.hostname.includes('localhost');

// In production: go through PHP simple.php; locally: use the Node.js admin API
const certApi = {
  list: (eventId) =>
    IS_PROD
      ? api.get(`/simple.php?action=event-certificates&event_id=${eventId}`)
      : axios.get(`/api/admin/events/${eventId}/certificates`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
  create: (eventId, data) =>
    IS_PROD
      ? api.post(`/simple.php?action=event-certificates&event_id=${eventId}`, data)
      : axios.post(`/api/admin/events/${eventId}/certificates`, data, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
  update: (eventId, certId, data) =>
    IS_PROD
      ? api.post(`/simple.php?action=event-certificates&cert_id=${certId}&_method=PUT`, data)
      : axios.put(`/api/admin/events/${eventId}/certificates/${certId}`, data, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
  remove: (eventId, certId) =>
    IS_PROD
      ? api.post(`/simple.php?action=event-certificates&cert_id=${certId}&_method=DELETE`)
      : axios.delete(`/api/admin/events/${eventId}/certificates/${certId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
};

const EMPTY_FORM = {
  cert_title: '',
  cert_order: 1,
  logo_filename: 'deped-logo.png',
  header_republic: 'Republic of the Philippines',
  header_department: 'Department of Education',
  header_region: 'Region XII – SOCCSKSARGEN',
  header_address: 'Regional Center, Carpenter Hill, City of Koronadal, South Cotabato',
  body_text: '',
  closing_text: '',
  signatory_name: '',
  signatory_title: '',
  esign_filename: '',
  is_active: 1,
};

const PLACEHOLDER_HINT = '{{salutation}} = Mr./Ms.   {{name}} = FULL NAME   {{first_name}}   {{last_name}}\nLines wrapped in **bold text** render in bold.';

const CertificateTemplates = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [certificates, setCertificates] = useState([]);
  const [templateFiles, setTemplateFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCert, setEditingCert] = useState(null); // null = new
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Fetch events list
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await api.get('/simple.php?action=events');
        if (res.data.success) setEvents(res.data.data);
      } catch {
        toast.error('Failed to load events');
      }
    };

    const fetchTemplateFiles = async () => {
      try {
        // Only works locally (Node.js admin API); in production files are managed via file manager
        if (!IS_PROD) {
          const res = await axios.get('/api/admin/templates/files', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          if (res.data.success) setTemplateFiles(res.data.files);
        }
      } catch {
        // Non-critical
      }
    };

    fetchEvents();
    fetchTemplateFiles();
  }, []);

  const fetchCertificates = useCallback(async (eventId) => {
    if (!eventId) return;
    setLoading(true);
    try {
      const res = await certApi.list(eventId);
      if (res.data.success) setCertificates(res.data.data);
    } catch {
      toast.error('Failed to load certificate templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEventId) fetchCertificates(selectedEventId);
    else setCertificates([]);
  }, [selectedEventId, fetchCertificates]);

  const openNew = () => {
    setEditingCert(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (cert) => {
    setEditingCert(cert);
    setForm({
      cert_title: cert.cert_title || '',
      cert_order: cert.cert_order || 1,
      logo_filename: cert.logo_filename || 'deped-logo.png',
      header_republic: cert.header_republic || 'Republic of the Philippines',
      header_department: cert.header_department || 'Department of Education',
      header_region: cert.header_region || '',
      header_address: cert.header_address || '',
      body_text: cert.body_text || '',
      closing_text: cert.closing_text || '',
      signatory_name: cert.signatory_name || '',
      signatory_title: cert.signatory_title || '',
      esign_filename: cert.esign_filename || '',
      is_active: cert.is_active ?? 1,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCert(null);
    setForm(EMPTY_FORM);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedEventId) return;
    setSaving(true);
    try {
      if (editingCert) {
        await certApi.update(selectedEventId, editingCert.cert_id, form);
        toast.success('Certificate template updated');
      } else {
        await certApi.create(selectedEventId, { ...form, events_id: selectedEventId });
        toast.success('Certificate template created');
      }
      closeModal();
      fetchCertificates(selectedEventId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save certificate template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cert) => {
    if (!window.confirm(`Delete "${cert.cert_title}"? This cannot be undone.`)) return;
    try {
      await certApi.remove(selectedEventId, cert.cert_id);
      toast.success('Certificate template deleted');
      fetchCertificates(selectedEventId);
    } catch {
      toast.error('Failed to delete certificate template');
    }
  };

  const toggleActive = async (cert) => {
    try {
      await certApi.update(selectedEventId, cert.cert_id, {
        ...cert,
        is_active: cert.is_active ? 0 : 1,
      });
      fetchCertificates(selectedEventId);
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="container" style={{ padding: '24px 16px' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <Award size={28} color="#3b82f6" />
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>
            Certificate Templates
          </h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
            Manage per-event certificate templates with custom signatories and content
          </p>
        </div>
      </div>

      {/* Event Selector */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        marginBottom: '20px',
      }}>
        <label style={{ fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
          Select Event
        </label>
        <select
          value={selectedEventId}
          onChange={e => setSelectedEventId(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '480px',
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1.5px solid #e2e8f0',
            fontSize: '0.95rem',
            color: '#1e293b',
          }}
        >
          <option value="">— Choose an event —</option>
          {events.map(ev => (
            <option key={ev.events_id} value={ev.events_id}>{ev.event_name}</option>
          ))}
        </select>
      </div>

      {/* Certificate List */}
      {selectedEventId && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
              Certificates for this event
            </h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => fetchCertificates(selectedEventId)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '6px' }}
              >
                <RefreshCw size={16} />
              </button>
              <button
                onClick={openNew}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: '#3b82f6', color: 'white', border: 'none',
                  borderRadius: '8px', padding: '8px 16px', cursor: 'pointer',
                  fontWeight: '600', fontSize: '0.9rem',
                }}
              >
                <Plus size={16} /> Add Certificate
              </button>
            </div>
          </div>

          {loading ? (
            <p style={{ color: '#64748b', textAlign: 'center', padding: '32px' }}>Loading...</p>
          ) : certificates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              <Award size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
              <p style={{ margin: 0 }}>No certificate templates yet. Click "Add Certificate" to create one.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {certificates.map(cert => (
                <div key={cert.cert_id} style={{
                  border: `1.5px solid ${cert.is_active ? '#bfdbfe' : '#e2e8f0'}`,
                  borderRadius: '10px',
                  padding: '16px',
                  background: cert.is_active ? '#f0f9ff' : '#f8fafc',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <span style={{
                          background: '#3b82f6', color: 'white',
                          borderRadius: '4px', padding: '2px 8px',
                          fontSize: '0.75rem', fontWeight: '700',
                        }}>
                          #{cert.cert_order}
                        </span>
                        <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '1rem' }}>
                          {cert.cert_title}
                        </span>
                        <span style={{
                          fontSize: '0.75rem',
                          color: cert.is_active ? '#059669' : '#94a3b8',
                          fontWeight: '600',
                        }}>
                          {cert.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                        <span>Logo: <strong>{cert.logo_filename || '—'}</strong></span>
                        <span style={{ marginLeft: '16px' }}>E-sign: <strong>{cert.esign_filename || '—'}</strong></span>
                        {cert.signatory_name && (
                          <span style={{ marginLeft: '16px' }}>Signatory: <strong>{cert.signatory_name}</strong></span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => toggleActive(cert)}
                        title={cert.is_active ? 'Deactivate' : 'Activate'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: cert.is_active ? '#059669' : '#94a3b8' }}
                      >
                        {cert.is_active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                      </button>
                      <button
                        onClick={() => openEdit(cert)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6' }}
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(cert)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '16px',
        }}>
          <div style={{
            background: 'white', borderRadius: '16px',
            width: '100%', maxWidth: '680px',
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            <div style={{ padding: '24px', borderBottom: '1px solid #e2e8f0' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1e293b' }}>
                {editingCert ? 'Edit Certificate Template' : 'New Certificate Template'}
              </h2>
            </div>

            <form onSubmit={handleSave} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Title + Order */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Certificate Title *</label>
                  <input
                    name="cert_title"
                    value={form.cert_title}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Certificate of Attendance"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Order</label>
                  <select name="cert_order" value={form.cert_order} onChange={handleChange} style={{ ...inputStyle, width: '80px' }}>
                    <option value={1}>1st</option>
                    <option value={2}>2nd</option>
                  </select>
                </div>
              </div>

              {/* Logo + E-sign */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Logo File</label>
                  <select name="logo_filename" value={form.logo_filename} onChange={handleChange} style={inputStyle}>
                    <option value="">— No logo —</option>
                    {templateFiles.map(f => <option key={f} value={f}>{f}</option>)}
                    {/* Allow typing a custom name if not in list */}
                    {form.logo_filename && !templateFiles.includes(form.logo_filename) && (
                      <option value={form.logo_filename}>{form.logo_filename} (current)</option>
                    )}
                  </select>
                  <input
                    name="logo_filename"
                    value={form.logo_filename}
                    onChange={handleChange}
                    placeholder="or type filename, e.g. deped-logo.png"
                    style={{ ...inputStyle, marginTop: '6px', fontSize: '0.82rem' }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>E-Signature File</label>
                  <select name="esign_filename" value={form.esign_filename} onChange={handleChange} style={inputStyle}>
                    <option value="">— No e-sign —</option>
                    {templateFiles.map(f => <option key={f} value={f}>{f}</option>)}
                    {form.esign_filename && !templateFiles.includes(form.esign_filename) && (
                      <option value={form.esign_filename}>{form.esign_filename} (current)</option>
                    )}
                  </select>
                  <input
                    name="esign_filename"
                    value={form.esign_filename}
                    onChange={handleChange}
                    placeholder="or type filename, e.g. esign-director.png"
                    style={{ ...inputStyle, marginTop: '6px', fontSize: '0.82rem' }}
                  />
                </div>
              </div>

              {/* Certificate Header */}
              <div style={{
                border: '1.5px solid #e2e8f0',
                borderRadius: '10px',
                padding: '16px',
                background: '#f8fafc',
              }}>
                <label style={{ ...labelStyle, color: '#1e293b', marginBottom: '12px' }}>
                  Certificate Header
                  <span style={{ fontWeight: '400', color: '#64748b', fontSize: '0.78rem', marginLeft: '8px' }}>
                    (printed at the top of the certificate)
                  </span>
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <label style={labelStyle}>Republic / Country Line</label>
                    <input
                      name="header_republic"
                      value={form.header_republic}
                      onChange={handleChange}
                      placeholder="Republic of the Philippines"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Department / Agency</label>
                    <input
                      name="header_department"
                      value={form.header_department}
                      onChange={handleChange}
                      placeholder="Department of Education"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Region / Division</label>
                    <input
                      name="header_region"
                      value={form.header_region}
                      onChange={handleChange}
                      placeholder="Region XII – SOCCSKSARGEN"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Address</label>
                    <input
                      name="header_address"
                      value={form.header_address}
                      onChange={handleChange}
                      placeholder="Regional Center, Carpenter Hill, City of Koronadal, South Cotabato"
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              {/* Body Text */}
              <div>
                <label style={labelStyle}>Body Text</label>
                <p style={{ margin: '0 0 6px', fontSize: '0.78rem', color: '#6b7280', background: '#f1f5f9', padding: '6px 10px', borderRadius: '6px', whiteSpace: 'pre-line' }}>
                  {PLACEHOLDER_HINT}
                </p>
                <textarea
                  name="body_text"
                  value={form.body_text}
                  onChange={handleChange}
                  rows={6}
                  placeholder={`This is to certify that {{salutation}}\n**{{name}}**\nattended the Regional Orientation on April 24, 2026.`}
                  style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }}
                />
              </div>

              {/* Closing Text */}
              <div>
                <label style={labelStyle}>Closing Text</label>
                <input
                  name="closing_text"
                  value={form.closing_text}
                  onChange={handleChange}
                  placeholder="Given this 24th day of April 2026 in the City of Koronadal, South Cotabato, Philippines."
                  style={inputStyle}
                />
              </div>

              {/* Signatory */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Signatory Name</label>
                  <input
                    name="signatory_name"
                    value={form.signatory_name}
                    onChange={handleChange}
                    placeholder="e.g. ATTY. ALBERTO T. ESCOBARTE, CESO II"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Signatory Title</label>
                  <input
                    name="signatory_title"
                    value={form.signatory_title}
                    onChange={handleChange}
                    placeholder="e.g. Director VI"
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Active toggle */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={!!form.is_active}
                  onChange={handleChange}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontWeight: '600', color: '#374151' }}>Active (send this certificate to participants)</span>
              </label>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    padding: '10px 20px', borderRadius: '8px',
                    border: '1.5px solid #e2e8f0', background: 'white',
                    cursor: 'pointer', fontWeight: '600', color: '#374151',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '10px 24px', borderRadius: '8px',
                    background: '#3b82f6', color: 'white', border: 'none',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: '600', opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? 'Saving...' : editingCert ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '6px',
  fontSize: '0.875rem',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1.5px solid #e2e8f0',
  fontSize: '0.9rem',
  color: '#1e293b',
  boxSizing: 'border-box',
};

export default CertificateTemplates;
